import { useEffect, useEffectEvent } from 'react';
import type { useEnvironmentSettings } from '@/hooks/use-environment-settings';
import { workspaceDirectoryStore } from '@/lib/assistants/directory';
import { PREVIEW_WORKSPACE_DIRECTORY } from '@/lib/assistants/preview-directory';
import { desktopPreviewState } from '@/lib/desktop-preview-state';
import {
  previewBoard,
  previewBoardName,
  previewChildPlacements,
} from '@/lib/desktop-preview-transcript';
import { dockArtifact } from '@/lib/desktop-shell/open-artifact';
import { useDesktopShellStoreApi } from '@/lib/desktop-shell/store-context';
import { forceOffline } from '@/lib/online-status';

/**
 * `/desktop-preview/chat?board=<Board>` (preview only): opens what the board shows over its
 * transcript — the rewind or environment dialog, the handoff, offline with a queued message, the
 * conversation as a workflow's task — and gives the preview its cards and widget data.
 */
export function PreviewBoardEffects({
  environment,
}: {
  environment: ReturnType<typeof useEnvironmentSettings>;
}) {
  const store = useDesktopShellStoreApi();
  // Read once, as the preview opens (with the store and settings of that moment).
  const openBoard = useEffectEvent((): (() => void) | undefined => {
    const board = previewBoard(previewBoardName(window.location));
    if (board === null) return;
    desktopPreviewState.setState({
      cards: null,
      widgetData: {},
      turnFailure: null,
      branchBehind: null,
      artifacts: null,
      artifactHtml: {},
      artifactPointed: {},
      artifactVariants: {},
      inCloud: false,
      emptySandbox: false,
      chatStatus: null,
      ...board.preview,
    });
    const directory = workspaceDirectoryStore();
    if (directory.getState().projects.length === 0)
      directory.setState({ ...PREVIEW_WORKSPACE_DIRECTORY, status: 'ready' });
    const state = store.getState();
    // The transcript is set as the preview opens: what opens over it waits for it.
    const timer = window.setTimeout(() => {
      switch (board.open) {
        case 'rewind':
          state.setConversationState(state.chatId, {
            pendingRewind: {
              message: { text: 'La CI est trop lente, regarde surtout le build Docker', files: [] },
              messageId: 'ci-user',
              editedIndex: 0,
              files: [
                {
                  path: '.github/workflows/ci.yml',
                  status: 'modified',
                  additions: 9,
                  deletions: 2,
                },
                { path: 'Dockerfile', status: 'modified', additions: 4, deletions: 1 },
                { path: 'package.json', status: 'modified', additions: 1, deletions: 1 },
                { path: 'turbo.json', status: 'modified', additions: 3, deletions: 0 },
              ],
              turnCount: 3,
              cancel: () => undefined,
            },
          });
          break;
        case 'environment':
          environment.setEnvironmentDraft((current) => ({
            ...current,
            name: 'crowdex',
            setupScript:
              'corepack enable\npnpm install --frozen-lockfile\npnpm --filter db migrate',
            actions: ['dev', 'test', 'lint'].map((name) => ({
              id: `preview-${name}`,
              name,
              icon: name === 'test' ? 'test' : 'play',
              script: `pnpm ${name}`,
              scripts: {},
            })),
          }));
          environment.setEnvironmentEnvText(
            'NODE_ENV=development\nDATABASE_URL=postgres://localhost/crowdex\nSTRIPE_KEY=sk_test_preview',
          );
          environment.setEnvironmentDialogOpen(true);
          break;
        case 'handoff':
          state.setLocalHandoffOpen(true);
          break;
        case 'offline':
          forceOffline(true);
          state.setQueuedFollowUps((current) => ({
            ...current,
            [state.chatId]: [
              { id: 'preview-offline', message: { text: 'Et le build Docker ?', files: [] } },
            ],
          }));
          break;
        case 'artifact':
          // Its Design view open wide, the button pointed at (its composer's, preview data).
          dockArtifact(store, 'preview-hero');
          break;
        case 'cloud-project-missing':
          state.setExecutionMode('cloud');
          state.setWorkspace('/workspace');
          state.setCloudProvisionError('Crowdex Cloud keeps no copy of this project.');
          state.setCloudProjectMissing('/workspace/repository/local/crowdex');
          break;
        case 'child':
          directory.setState({ placements: previewChildPlacements(state.chatId) });
          break;
        default:
          break;
      }
    }, 300);
    return () => window.clearTimeout(timer);
  });
  useEffect(() => openBoard(), []);
  return null;
}
